import { Location, FilterState } from '../types';
import { initialLocations } from '../data/initialLocations';
import { supabase } from './supabase';

const BASE_URL = `${import.meta.env.VITE_SUPABASE_URL}/rest/v1/locations`;
const BUCKET_NAME = import.meta.env.VITE_STORAGE_BUCKET || 'location-images';
const TIMEOUT_MS = 10000;
const MAX_RETRIES = 3;
const INITIAL_RETRY_DELAY = 1000;
export const LOCATIONS_PER_PAGE = 20;

const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Helper function to get authenticated headers
const getAuthHeaders = async () => {
  const { data: { session } } = await supabase.auth.getSession();
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'apikey': import.meta.env.VITE_SUPABASE_ANON_KEY,
    'Prefer': 'return=representation'
  };

  if (session?.access_token) {
    headers['Authorization'] = `Bearer ${session.access_token}`;
  }

  return headers;
};

// Helper function to process image URLs
const processImageUrls = (images: string[]): string[] => {
  if (!Array.isArray(images)) {
    console.warn('Images is not an array:', images);
    return [];
  }

  return images.map((image: string) => {
    if (!image || typeof image !== 'string') {
      console.warn('Invalid image path:', image);
      return '';
    }

    // If it's already a full URL, return as is
    if (image.startsWith('http://') || image.startsWith('https://')) {
      return image;
    }

    // If it's a relative path, construct the full URL using Supabase storage
    try {
      const { data } = supabase.storage
        .from(BUCKET_NAME)
        .getPublicUrl(image);
      
      console.log('Generated public URL for image:', image, '->', data.publicUrl);
      return data.publicUrl;
    } catch (error) {
      console.error('Error generating public URL for image:', image, error);
      return '';
    }
  }).filter(url => url !== ''); // Remove empty URLs
};

export const fetchWithRetry = async (
  url: string,
  options: RequestInit = {},
  retries = MAX_RETRIES,
  delay = INITIAL_RETRY_DELAY
): Promise<Response> => {
  try {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), TIMEOUT_MS);

    const headers = await getAuthHeaders();

    const response = await fetch(url, {
      ...options,
      signal: controller.signal,
      headers: {
        ...headers,
        ...options.headers
      }
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw new Error(
        `HTTP error! status: ${response.status}${
          errorData ? `, message: ${JSON.stringify(errorData)}` : ''
        }`
      );
    }

    return response;
  } catch (error) {
    if (retries === 0) throw error;

    console.warn(`Retry attempt ${MAX_RETRIES - retries + 1} of ${MAX_RETRIES}. Waiting ${delay}ms...`);
    await wait(delay);

    return fetchWithRetry(url, options, retries - 1, delay * 2);
  }
};

export const getLocations = async (
  isAdmin: boolean = false,
  page: number = 1,
  limit: number = LOCATIONS_PER_PAGE,
  filters: Partial<FilterState> = {}
): Promise<{ locations: Location[]; total: number }> => {
  try {
    console.log('Fetching locations with filters:', filters);
    
    // Use Supabase SDK instead of direct fetch for better auth handling
    let query = supabase.from('locations').select('*', { count: 'exact' });

    // Apply filters
    if (!isAdmin) {
      query = query.eq('status', 'approved');
    }
    
    if (filters.category && filters.category !== 'all') {
      query = query.eq('category', filters.category);
    }
    
    if (filters.searchQuery && filters.searchQuery.trim()) {
      query = query.ilike('name', `%${filters.searchQuery.trim()}%`);
    }
    
    if (filters.tags && filters.tags.length > 0) {
      query = query.contains('tags', filters.tags);
    }

    // Apply sorting
    if (filters.sortBy === 'newest') {
      query = query.order('created_at', { ascending: false });
    } else {
      query = query.order('created_at', { ascending: false }); // Default sort
    }

    // Apply pagination
    const offset = (page - 1) * limit;
    query = query.range(offset, offset + limit - 1);

    const { data, error, count } = await query;

    if (error) {
      console.error('Supabase query error:', error);
      throw new Error(`Database error: ${error.message}`);
    }

    if (!Array.isArray(data)) {
      throw new Error('Invalid data format received from server');
    }

    console.log('Raw data from database:', data);

    const locations = data.map((location: any) => {
      console.log('Processing location:', location.name, 'with images:', location.images);
      
      // Process images - they should already be full URLs now
      const rawImages = location.images || [];
      console.log('Raw images for', location.name, ':', rawImages);
      
      // Since we now store full URLs, we don't need to process them
      // But we still filter out any invalid entries
      const processedImages = rawImages.filter((img: string) => 
        img && typeof img === 'string' && img.startsWith('http')
      );
      
      console.log('Processed images for', location.name, ':', processedImages);

      return {
        id: location.id,
        name: location.name,
        description: location.description,
        shortDescription: location.short_description,
        category: location.category,
        coordinates: location.coordinates,
        images: processedImages,
        tags: location.tags || [],
        status: location.status,
        created_at: location.created_at,
        updated_at: location.updated_at,
        is_verified: location.is_verified,
        search_vector: location.search_vector,
        short_id: location.short_id
      };
    });

    console.log('Final processed locations:', locations);

    const total = count || 0;

    return { locations, total };
  } catch (error) {
    console.error('Error fetching locations:', {
      message: error instanceof Error ? error.message : 'Unknown error',
      error
    });

    // Fallback to initial locations only in development
    if (import.meta.env.DEV) {
      console.warn('Using fallback data in development mode');
      return { locations: initialLocations, total: initialLocations.length };
    }

    // In production, re-throw the error
    throw error;
  }
};

export const getAllLocations = async (isAdmin: boolean = false): Promise<Location[]> => {
  const allLocations: Location[] = [];
  let page = 1;
  const limit = 100; // Larger page size for efficiency
  
  while (true) {
    const { locations, total } = await getLocations(isAdmin, page, limit);
    allLocations.push(...locations);
    
    if (allLocations.length >= total || locations.length < limit) {
      break;
    }
    page++;
  }
  
  return allLocations;
};